<script setup>
import { ref, computed } from 'vue'
import { provideDocExpansion } from '@/composables/useDocExpansion'
import DocSection from '@/components/Docs/Section.vue'
import CodeBlock from '@/components/Docs/CodeBlock.vue'
import JsonBlock from '@/components/Docs/JsonBlock.vue'
import StringParam from '@/components/Docs/Parameters/StringParam.vue'
import MultiSelectParam from '@/components/Docs/Parameters/MultiSelectParam.vue'
import EndpointBlock from '@/components/Docs/EndpointBlock.vue'
import BasicAttribute from '@/components/Docs/Attributes/BasicAttribute.vue'

defineProps({ anchor: String })

// Request parameters
let request_query = ref("8.8.8.8")
let request_limit = ref("100")
let request_offset = ref("0")
let request_log_types = ref([])

// Log type options
const logTypeOptions = [
  { value: 'conn', label: 'Connection Logs' },
  { value: 'dns', label: 'DNS Queries' },
  { value: 'http', label: 'HTTP Traffic' },
  { value: 'ssl', label: 'SSL/TLS' },
  { value: 'weird', label: 'Anomalies' },
  { value: 'x509', label: 'Certificates' },
  { value: 'files', label: 'File Transfers' },
  { value: 'notice', label: 'Notices' }
]

// Build request body for POST request
let request_body = computed(() => {
  return {
    query: request_query.value,
    limit: parseInt(request_limit.value) || 100,
    offset: parseInt(request_offset.value) || 0,
    log_types: request_log_types.value
  }
})

// Master expand/collapse control
const { triggerExpandAll } = provideDocExpansion()
const allExpanded = ref(false)

const toggleAllExpansion = () => {
  allExpanded.value = !allExpanded.value
  triggerExpandAll(allExpanded.value)
}
</script>

<template>
  <div>
    <doc-section title="Search" :anchor="anchor">
      <template v-slot:left>
        <!-- Description -->
        <div class="mb-5">
          <p>
            Search through ingested security event logs using query-string syntax.
            This endpoint supports complex queries across multiple log types including
            connection logs, DNS queries, HTTP traffic, and more.
          </p>
          <p>
            Results include event documents, a temporal histogram, and log-type
            distributions. Deep pagination beyond <code>offset + limit = 10000</code>
            uses the <code>search_after</code> cursor returned in the response.
          </p>
          <p>
            Sources and log types are scoped to your organization's grants.
          </p>
        </div>

        <!-- Request Parameters -->
        <div class="mb-5">
          <h4 class="mb-4">Request parameters</h4>

          <StringParam
            class="mb-3"
            name="Query"
            :required="true"
            description="Lucene query string (e.g., '8.8.8.8', 'host:example.com')"
            type="string"
            :value="request_query"
            @value="(v) => request_query = v"
          />

          <StringParam
            class="mb-3"
            name="Limit"
            :required="false"
            description="Maximum number of results to return (default: 100, max: 10000)"
            type="integer"
            :value="request_limit"
            @value="(v) => request_limit = v"
          />

          <StringParam
            class="mb-3"
            name="Offset"
            :required="false"
            description="Number of results to skip for pagination (default: 0)"
            type="integer"
            :value="request_offset"
            @value="(v) => request_offset = v"
          />

          <MultiSelectParam
            class="mb-3"
            name="Log Types"
            :required="false"
            description="Filter results by specific log types (leave empty for all types)"
            :options="logTypeOptions"
            :value="request_log_types"
            @value="(v) => request_log_types = v"
          />
        </div>

        <!-- Response Attributes -->
        <div>
          <div class="d-flex justify-content-between align-items-center mb-4">
            <h4 class="mb-0">Response attributes</h4>
            <button @click="toggleAllExpansion" class="btn-expand-collapse">
              <font-awesome-icon :icon="['fas', allExpanded ? 'compress-alt' : 'expand-alt']" />
              {{ allExpanded ? 'Collapse All' : 'Expand All' }}
            </button>
          </div>

          <BasicAttribute
            name="status"
            type="string"
            description="Response status (success or error)"
          />

          <BasicAttribute
            name="data"
            type="object"
            description="Response data container"
            :depth="1"
          >
            <BasicAttribute
              name="results"
              type="array"
              description="Array of event source documents (contains log-specific fields like ts, uid, id.orig_h, id.resp_h, proto, etc.)"
              :depth="2"
            >
              <BasicAttribute
                name="[item]"
                type="object"
                description="Event source document with fields varying by log type (conn, dns, http, ssl, etc.)"
              />
            </BasicAttribute>

            <BasicAttribute
              name="total"
              type="integer"
              description="Total number of matching documents"
            />

            <BasicAttribute
              name="histogram"
              type="array"
              description="Temporal distribution of events"
              :depth="2"
            >
              <BasicAttribute
                name="[item]"
                type="object"
                description="Histogram bucket"
                :depth="3"
              >
                <BasicAttribute
                  name="timestamp"
                  type="integer"
                  description="Unix timestamp in milliseconds"
                />
                <BasicAttribute
                  name="count"
                  type="integer"
                  description="Number of events in this time bucket"
                />
              </BasicAttribute>
            </BasicAttribute>

            <BasicAttribute
              name="log_types"
              type="array"
              description="Distribution of results across log types"
              :depth="2"
            >
              <BasicAttribute
                name="[item]"
                type="object"
                description="Log type bucket"
                :depth="3"
              >
                <BasicAttribute
                  name="log_type"
                  type="string"
                  description="Log type name (e.g., 'conn', 'dns', 'http')"
                />
                <BasicAttribute
                  name="count"
                  type="integer"
                  description="Number of events of this log type"
                />
              </BasicAttribute>
            </BasicAttribute>

            <BasicAttribute
              name="limit"
              type="integer"
              description="Applied limit value"
            />

            <BasicAttribute
              name="offset"
              type="integer"
              description="Applied offset value"
            />
          </BasicAttribute>
        </div>
      </template>

      <template v-slot:right>
        <!-- Endpoint Display -->
        <endpoint-block
          title="/query/event/search"
          method="POST"
          name="Event Search"
          query="/query/event/search"
          :body="request_body"
        />

        <!-- Response Example -->
        <json-block title="Response" class="mt-3">
{
  "status": "success",
  "data": {
    "results": [
      {
        "ts": 1704067200.123456,
        "uid": "CxYz123456789",
        "id.orig_h": "192.168.1.100",
        "id.orig_p": 54321,
        "id.resp_h": "8.8.8.8",
        "id.resp_p": 53,
        "proto": "udp",
        "query": "example.com",
        "qtype_name": "A",
        "rcode_name": "NOERROR",
        "answers": ["93.184.216.34"]
      }
    ],
    "total": 42,
    "histogram": [
      {
        "timestamp": 1704067200000,
        "count": 15
      },
      {
        "timestamp": 1704070800000,
        "count": 27
      }
    ],
    "log_types": [
      {
        "log_type": "dns",
        "count": 30
      },
      {
        "log_type": "conn",
        "count": 12
      }
    ],
    "limit": 100,
    "offset": 0
  }
}
        </json-block>
      </template>
    </doc-section>
  </div>
</template>

<style scoped>
.btn-expand-collapse {
  background-color: transparent;
  border: 1px solid var(--border-subtle);
  color: var(--text-faint);
  padding: 0.5rem 1rem;
  border-radius: var(--radius-md);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-expand-collapse:hover {
  background-color: var(--surface-overlay-2);
  color: var(--text-primary);
}
</style>
